<template>
  <div class="ring-chart-container">
    <div class="ring-chart" :style="{ width: size + 'px', height: size + 'px' }">
      <svg :width="size" :height="size" class="ring-svg">
        <!-- Background circle -->
        <circle
          :cx="center"
          :cy="center"
          :r="radius"
          fill="none"
          :stroke="backgroundColor"
          :stroke-width="strokeWidth"
          class="background-ring"
        />

        <!-- Progress circle -->
        <circle
          :cx="center"
          :cy="center"
          :r="radius"
          fill="none"
          :stroke="color"
          :stroke-width="strokeWidth"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="strokeDashoffset"
          class="progress-ring"
          stroke-linecap="round"
          transform="rotate(-90 50 50)"
        />
      </svg>

      <!-- Center content -->
      <div class="ring-content">
        <div class="ring-percentage">{{ Math.round(percentage) }}%</div>
        <div class="ring-label">{{ label }}</div>
        <div class="ring-details">
          <div class="ring-detail">{{ used }} / {{ total }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  percentage: number; // 0-100
  used: string;
  total: string;
  label?: string;
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Usage',
  size: 200,
  strokeWidth: 12,
  color: '#3b82f6',
  backgroundColor: '#e5e7eb',
});

const center = computed(() => props.size / 2);
const radius = computed(() => (props.size - props.strokeWidth) / 2);
const circumference = computed(() => 2 * Math.PI * radius.value);
const strokeDashoffset = computed(() => {
  return circumference.value - (props.percentage / 100) * circumference.value;
});

// Dynamic color based on percentage
const color = computed(() => {
  if (props.percentage >= 90) return '#ef4444'; // Red for critical
  if (props.percentage >= 75) return '#f59e0b'; // Orange for warning
  return props.color; // Default color for normal
});
</script>

<style scoped>
.ring-chart-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.ring-chart {
  position: relative;
  display: inline-block;
}

.ring-svg {
  transform: rotate(0deg);
}

.background-ring {
  opacity: 0.2;
}

.progress-ring {
  transition:
    stroke-dashoffset 0.8s ease-in-out,
    stroke 0.3s ease;
}

.ring-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  pointer-events: none;
}

.ring-percentage {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  line-height: 1;
}

.ring-label {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.25rem;
  font-weight: 500;
}

.ring-details {
  margin-top: 0.5rem;
}

.ring-detail {
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 500;
}

/* Responsive sizing */
@media (max-width: 640px) {
  .ring-percentage {
    font-size: 1.5rem;
  }

  .ring-label {
    font-size: 0.75rem;
  }

  .ring-detail {
    font-size: 0.625rem;
  }
}
</style>
